import { useCallback, useEffect, useMemo, useState } from 'react'
import { hexToString, type Address, type Hex, type PublicClient } from 'viem'
import { getAddress } from 'viem'
import { DATA_KEYS, LSP0_ABI } from '../constants'
import {
  allowedCallsTupleBitmapLabels,
  decodePermissions,
  getAllowedCallsDataKey,
  getAllowedDataKeysDataKey,
  getArrayIndexDataKey,
  getPermissionsDataKey,
  getControllerLabelDataKey,
} from '../utils'

export type DecodedAllowedCall = {
  callTypes: string[]
  address: Address | 'any'
  interfaceId: Hex | 'any'
  selector: Hex | 'any'
  rawEntry: Hex
}

export type ControllerPermissions = {
  address: Address
  controllerLabel: string | null
  permissionsHex: Hex
  permissionsBigInt: bigint
  permissionKeys: string[]
  allowedCallsHex: Hex | null
  allowedDataKeysHex: Hex | null
  decodedAllowedCalls: DecodedAllowedCall[] | null
  decodedAllowedDataKeys: Hex[] | null
}

function getFriendlyControllersError(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err)
  const lower = message.toLowerCase()
  if (
    lower.includes('failed to fetch') ||
    lower.includes('http request failed') ||
    lower.includes('network error')
  ) {
    return 'Could not refresh controllers right now (RPC network issue). Please try again.'
  }
  return message || 'Failed to load controllers'
}

function hexToBigIntSafe(hex: Hex | null | undefined): bigint {
  if (!hex || hex === '0x') return 0n
  try {
    return BigInt(hex)
  } catch {
    return 0n
  }
}

function decodeCompactBytesArray(hex: Hex): Hex[] {
  if (!hex || hex === '0x') return []
  const clean = hex.toLowerCase()
  const out: Hex[] = []
  let i = 2 // skip 0x

  while (i + 4 <= clean.length) {
    const lenHex = clean.slice(i, i + 4)
    const lenBytes = Number.parseInt(lenHex, 16)
    if (!Number.isFinite(lenBytes) || lenBytes <= 0) break
    i += 4

    const chunk = clean.slice(i, i + lenBytes * 2)
    if (chunk.length < lenBytes * 2) break
    out.push((`0x${chunk}`) as Hex)
    i += lenBytes * 2
  }

  return out
}

function decodeCallTypes(bitmap: number): string[] {
  const types = allowedCallsTupleBitmapLabels(bitmap)
  if (types.length === 0) types.push(`0x${bitmap.toString(16)}`)
  return types
}

function decodeAllowedCalls(hex: Hex): DecodedAllowedCall[] {
  const entries = decodeCompactBytesArray(hex)

  return entries
    .map((entry): DecodedAllowedCall | null => {
      const data = entry.slice(2)
      if (data.length < 64) return null

      const callTypesHex = data.slice(0, 8)
      const addressHex = data.slice(8, 48)
      const interfaceIdHex = data.slice(48, 56)
      const selectorHex = data.slice(56, 64)

      const callTypesBitmap = Number.parseInt(callTypesHex, 16)
      const isAnyAddress = addressHex === 'ffffffffffffffffffffffffffffffffffffffff'
      const isAnyInterface = interfaceIdHex === 'ffffffff'
      const isAnySelector = selectorHex === 'ffffffff'

      const address = isAnyAddress ? 'any' : getAddress((`0x${addressHex}`) as Hex)
      const interfaceId = isAnyInterface ? 'any' : ((`0x${interfaceIdHex}`) as Hex)
      const selector = isAnySelector ? 'any' : ((`0x${selectorHex}`) as Hex)

      return {
        callTypes: decodeCallTypes(callTypesBitmap),
        address,
        interfaceId,
        selector,
        rawEntry: entry,
      }
    })
    .filter((x): x is DecodedAllowedCall => x !== null)
}

function decodeAllowedDataKeys(hex: Hex): Hex[] {
  // AllowedERC725YDataKeys is a CompactBytesArray of key prefixes
  return decodeCompactBytesArray(hex)
}

export function useControllers({
  upAddress,
  publicClient,
  refreshToken,
}: {
  upAddress: Address | null
  publicClient: PublicClient | null
  refreshToken?: number
}) {
  const [controllers, setControllers] = useState<ControllerPermissions[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canLoad = useMemo(() => !!upAddress && !!publicClient, [upAddress, publicClient])

  const load = useCallback(async () => {
    if (!upAddress || !publicClient) return
    setIsLoading(true)
    setError(null)
    try {
      // 1) Read controllers length
      const lengthHex = (await publicClient.readContract({
        address: upAddress,
        abi: LSP0_ABI,
        functionName: 'getData',
        args: [DATA_KEYS['AddressPermissions[]'] as Hex],
      })) as Hex

      const length = Number(hexToBigIntSafe(lengthHex))
      if (!Number.isFinite(length) || length <= 0) {
        setControllers([])
        setIsLoading(false)
        return
      }

      // 2) Read all controller addresses in one batch
      const indexKeys: Hex[] = Array.from({ length }, (_, i) => getArrayIndexDataKey(i))
      const indexValues = (await publicClient.readContract({
        address: upAddress,
        abi: LSP0_ABI,
        functionName: 'getDataBatch',
        args: [indexKeys],
      })) as Hex[]

      const addresses: Address[] = indexValues
        .map((v) => {
          if (!v || v === '0x') return null
          // Stored as bytes20, but treat defensively (slice last 20 bytes)
          const last40 = v.slice(-40)
          const maybe = (`0x${last40}`) as Hex
          try {
            const addr = getAddress(maybe)
            if (addr === '0x0000000000000000000000000000000000000000') return null
            return addr
          } catch {
            return null
          }
        })
        .filter((x): x is Address => x !== null)

      if (addresses.length === 0) {
        setControllers([])
        setIsLoading(false)
        return
      }

      // 3) Read permissions + restrictions + label in one batch per controller
      const flatKeys: Hex[] = []
      for (const addr of addresses) {
        flatKeys.push(getPermissionsDataKey(addr))
        flatKeys.push(getAllowedCallsDataKey(addr))
        flatKeys.push(getAllowedDataKeysDataKey(addr))
        flatKeys.push(getControllerLabelDataKey(addr))
      }

      const flatValues = (await publicClient.readContract({
        address: upAddress,
        abi: LSP0_ABI,
        functionName: 'getDataBatch',
        args: [flatKeys],
      })) as Hex[]

      const next: ControllerPermissions[] = []
      for (let i = 0; i < addresses.length; i++) {
        const addr = addresses[i]
        const permissionsHex = (flatValues[i * 4 + 0] ?? '0x') as Hex
        const allowedCallsHexRaw = (flatValues[i * 4 + 1] ?? '0x') as Hex
        const allowedDataKeysHexRaw = (flatValues[i * 4 + 2] ?? '0x') as Hex
        const labelHex = (flatValues[i * 4 + 3] ?? '0x') as Hex

        const permissionsBigInt = hexToBigIntSafe(permissionsHex)
        const permissionKeys = decodePermissions(permissionsHex)

        const allowedCallsHex = allowedCallsHexRaw !== '0x' ? allowedCallsHexRaw : null
        const allowedDataKeysHex = allowedDataKeysHexRaw !== '0x' ? allowedDataKeysHexRaw : null

        if (permissionsBigInt === 0n) {
          continue
        }

        let controllerLabel: string | null = null
        if (labelHex && labelHex !== '0x') {
          try {
            controllerLabel = hexToString(labelHex).replace(/\u0000+$/g, '').trim() || null
          } catch {
            controllerLabel = null
          }
        }

        next.push({
          address: addr,
          controllerLabel,
          permissionsHex,
          permissionsBigInt,
          permissionKeys,
          allowedCallsHex,
          allowedDataKeysHex,
          decodedAllowedCalls: allowedCallsHex ? decodeAllowedCalls(allowedCallsHex) : null,
          decodedAllowedDataKeys: allowedDataKeysHex ? decodeAllowedDataKeys(allowedDataKeysHex) : null,
        })
      }

      setControllers(next)
      setIsLoading(false)
    } catch (err) {
      console.error('[useControllers] Failed to load controllers', err)
      setError(getFriendlyControllersError(err))
      setIsLoading(false)
    }
  }, [publicClient, upAddress])

  useEffect(() => {
    if (!canLoad) {
      setControllers([])
      setError(null)
      setIsLoading(false)
      return
    }
    load()
  }, [canLoad, load, refreshToken])

  return {
    controllers,
    isLoading,
    error,
    reload: load,
  }
}

